import React, { useState, useEffect, useCallback } from 'react';
import './SnakeGame.css';

const GRID_SIZE = 20;
const GAME_WIDTH = 280;
const GAME_HEIGHT = 280;
const TILE_SIZE = GAME_WIDTH / GRID_SIZE;

const getRandomFoodPosition = (snake) => {
  let newFoodPosition;
  do {
    newFoodPosition = {
      x: Math.floor(Math.random() * GRID_SIZE),
      y: Math.floor(Math.random() * GRID_SIZE),
    };
  } while (snake.some(segment => segment.x === newFoodPosition.x && segment.y === newFoodPosition.y));
  return newFoodPosition;
};

const SnakeGame = ({ gameApi }) => {
  const [snake, setSnake] = useState([{ x: 10, y: 10 }]);
  const [food, setFood] = useState(getRandomFoodPosition(snake));
  const [direction, setDirection] = useState({ x: 0, y: -1 }); // Start moving up
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);

  const resetGame = useCallback(() => {
    setSnake([{ x: 10, y: 10 }]);
    setFood(getRandomFoodPosition([{ x: 10, y: 10 }]));
    setDirection({ x: 0, y: -1 });
    setGameOver(false);
    setScore(0);
  }, []);

  useEffect(() => {
    gameApi.moveUp.current = () => { if (direction.y === 0) setDirection({ x: 0, y: -1 }); };
    gameApi.moveDown.current = () => { if (direction.y === 0) setDirection({ x: 0, y: 1 }); };
    gameApi.moveLeft.current = () => { if (direction.x === 0) setDirection({ x: -1, y: 0 }); };
    gameApi.moveRight.current = () => { if (direction.x === 0) setDirection({ x: 1, y: 0 }); };
    gameApi.reset.current = () => { if (gameOver) resetGame(); };
  }, [direction, gameOver, gameApi, resetGame]);

  useEffect(() => {
    if (gameOver) return;

    const gameLoop = setInterval(() => {
      const newSnake = [...snake];
      const head = { x: newSnake[0].x + direction.x, y: newSnake[0].y + direction.y };

      // Wall collision
      if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
        setGameOver(true);
        return;
      }

      // Self collision
      if (newSnake.some(segment => segment.x === head.x && segment.y === head.y)) {
        setGameOver(true);
        return;
      }

      newSnake.unshift(head);

      // Food collision
      if (head.x === food.x && head.y === food.y) {
        setScore(prev => prev + 1);
        setFood(getRandomFoodPosition(newSnake));
      } else {
        newSnake.pop();
      }

      setSnake(newSnake);
    }, 200);

    return () => clearInterval(gameLoop);
  }, [snake, direction, food, gameOver]);

  return (
    <div className="snake-game-container">
      {gameOver ? (
        <div className="game-over-screen">
          <h2>Game Over</h2>
          <p>Your Score: {score}</p>
          <p>Press A to play again.</p>
        </div>
      ) : (
        <div className="game-board" style={{ width: GAME_WIDTH, height: GAME_HEIGHT }}>
          {snake.map((segment, index) => (
            <div
              key={index}
              className="snake-segment"
              style={{ left: segment.x * TILE_SIZE, top: segment.y * TILE_SIZE, width: TILE_SIZE, height: TILE_SIZE }}
            />
          ))}
          <div
            className="food"
            style={{ left: food.x * TILE_SIZE, top: food.y * TILE_SIZE, width: TILE_SIZE, height: TILE_SIZE }}
          />
        </div>
      )}
      <div className="score">Score: {score}</div>
    </div>
  );
};

export default SnakeGame;